// =============================================================================
// Calendar — the month view and everything reachable from it. This file owns
// the state and the derived data (which events belong to this calendar,
// birthdays / holidays / to-dos as read-only entries, label overlays) and
// composes the pieces under ./calendar/: header, month grid carousel, day
// sheet, event form + detail, shared-calendar sheet, label manager and the
// series-scope choosers. Pure helpers live in ./calendar/utils.js,
// ./calendar/derivedEvents.js and ./calendar/seriesEdits.js.
// =============================================================================

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, CalendarDays, Bell } from 'lucide-react';
import BirthdayCalendar from './BirthdayCalendar';
import { expandEvents } from '../utils/recurrence';
import { useToast } from '../lib/Toast';
import { useTranslation } from '../lib/i18n';
import { useConfirm } from '../lib/ConfirmDialog';
import { useAuth } from '../lib/AuthContext';
import {
  getInviteUrl,
  listSharedCalendarMembers,
  setSharedCalendarNotifications,
  setSharedCalendarHolidays
} from '../lib/sharedCalendars';
import { deviceTimeZone } from '../lib/notificationPrefs';
import { loadProfile } from '../lib/profile';
import { defaultData } from '../utils/storage';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  setMonth,
  setYear,
  isSameDay,
  getYear,
  parseISO,
} from 'date-fns';
import { formatDate as fmt } from '../lib/dates';
import { weekdayLabels as localeWeekdays } from '../lib/i18n';
import CalendarHeader from './calendar/CalendarHeader';
import CreateSharedCalendarModal from './calendar/CreateSharedCalendarModal';
import SharedCalendarSheet from './calendar/SharedCalendarSheet';
import DayPanel from './calendar/DayPanel';
import MonthGrid from './calendar/MonthGrid';
import ActivityFeed from './calendar/ActivityFeed';
import EventForm from './calendar/EventForm';
import EventDetail from './calendar/EventDetail';
import { RecurringEditScopeModal, RecurringDeleteScopeModal } from './calendar/RecurringScopeModal';
import LabelManager from './calendar/LabelManager';
import { renderMemberAvatar } from './calendar/memberAvatar';
import { eventsForDay } from './calendar/utils';
import {
  buildBirthdayEvents,
  buildHolidayEvents,
  buildTodoEvents,
  buildTodoLabelOwners,
  applyLabelOverrides,
  buildActivityFeed
} from './calendar/derivedEvents';
import {
  editWholeSeries,
  editOccurrenceOnly,
  editFromOccurrenceOn,
  deleteOccurrenceOnly,
  deleteFromOccurrenceOn
} from './calendar/seriesEdits';

const Calendar = ({ data, updateData, reload, sharedCalendarId = null, homeSignal = 0, intent = null }) => {
  const { t, lang } = useTranslation();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());

  // Tapping the calendar's tab returns to the current month — sliding there
  // through the same carousel a swipe uses, then popping today's date so
  // the eye lands on it. `jumpTo` temporarily replaces the incoming panel
  // so the slide works even when today is many months away.
  const [jumpTo, setJumpTo] = useState(null);
  const [jumpBack, setJumpBack] = useState(false);
  const [popToday, setPopToday] = useState(false);
  const homeSignalRef = useRef(homeSignal);
  useEffect(() => {
    if (homeSignal === homeSignalRef.current) return;
    homeSignalRef.current = homeSignal;
    setSelectedDate(null);
    setDayPanelDate(null);

    const now = new Date();
    const sameMonth =
      now.getFullYear() === currentDate.getFullYear() &&
      now.getMonth() === currentDate.getMonth();
    if (sameMonth) {
      // Already here — just draw attention to today.
      setPopToday(true);
      setTimeout(() => setPopToday(false), 800);
      return;
    }

    const backwards = now < currentDate;
    const width = carouselRef.current?.offsetWidth || window.innerWidth;
    setJumpTo(now);
    setJumpBack(backwards);
    setSwipeAnim(true);
    setSwipeDx(backwards ? width : -width);
    setTimeout(() => {
      setCurrentDate(now);
      setSwipeAnim(false);
      setSwipeDx(0);
      setJumpTo(null);
      setPopToday(true);
      setTimeout(() => setPopToday(false), 800);
    }, 240);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeSignal]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  // Editing a repeating event asks what the change applies to — this one,
  // this and the following, or the whole series — like deleting does.
  const [recurringEditTarget, setRecurringEditTarget] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [recurringDeleteTarget, setRecurringDeleteTarget] = useState(null); // { baseId, occurrenceDate }
  const [showCreateShared, setShowCreateShared] = useState(false);
  const [newSharedName, setNewSharedName] = useState('');
  const [newSharedWithBirthdays, setNewSharedWithBirthdays] = useState(false);
  const [creatingShared, setCreatingShared] = useState(false);
  // Shared-calendar sub-view toggle: show BirthdayCalendar inline instead
  // of the month grid when the user taps the "Birthdays" button.
  const [showSharedBirthdays, setShowSharedBirthdays] = useState(false);
  // Top-level layout switch for this view: 'calendar' (the month grid) or
  // 'activity' (a chronological feed of recent audit + comment changes
  // across every shared calendar the user can see). Toggled via the bottom
  // segmented bar.
  const [viewMode, setViewMode] = useState('calendar');
  // Natural-language quick-add input ("Zahnarzt morgen 14:00"). Parsed
  // live with chrono-node; on Enter we build a real event and skip the
  // modal entirely.
  const toast = useToast();

  // Responsive breakpoint tracking. Used so multi-day-bar absolute offsets
  // (inline styles) and the optional week-number column stay in sync.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);
  // Tailwind's `md` (768px): iPad in either orientation, iPhone landscape,
  // a half-width browser window. From here the day panel is a column beside
  // the grid rather than a sheet over it (the shell shows a sidebar rail at
  // the same width), so one tap on a day opens it.
  const [isWide, setIsWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setIsWide(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);
  // Horizontal swipe state for the month carousel. While dragging we
  // translate a 3-wide strip (prev/current/next) so the adjacent months
  // peek in under the finger.
  const swipeRef = useRef(null); // { x, y, width }
  const carouselRef = useRef(null);
  // Width of the month grid itself. With the day column open beside it the
  // grid can be phone-wide on a tablet-wide screen, so its density
  // (week-number column, chip size) follows this rather than the viewport.
  // Below 560px it switches to the phone layout — that is roughly a 640px
  // window minus its padding, the narrowest the desktop grid was ever shown
  // at. Phones stay on the viewport rule (`isMobile`) so nothing changes
  // there; jsdom has no ResizeObserver, hence the guard.
  const COMPACT_GRID_PX = 560;
  const [gridWidth, setGridWidth] = useState(0);
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      setGridWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
    // The carousel only exists in the month view; re-attach when it returns.
  }, [viewMode, showSharedBirthdays]);
  const compactGrid = isMobile || (gridWidth > 0 && gridWidth < COMPACT_GRID_PX);
  const [swipeDx, setSwipeDx] = useState(0);
  const [swipeActive, setSwipeActive] = useState(false);
  const [swipeAnim, setSwipeAnim] = useState(false);
  // The date whose events panel is currently open above (phones) or beside
  // (md+) the calendar. Set when the user taps an already-selected day, or
  // any day on md+.
  const [dayPanelDate, setDayPanelDate] = useState(null);
  const [sharedSettings, setSharedSettings] = useState(null); // { members, inviteUrl, loading, copied }
  // uid → member info, used for "created by" avatars in shared-calendar views.
  const [membersById, setMembersById] = useState({});
  // ID of the event currently shown in the detail view (tap on a list item).
  const [viewingEventId, setViewingEventId] = useState(null);
  // The concrete occurrence date the user tapped when opening a recurring
  // event's detail view. Without this the detail/edit/delete flow only knows
  // the base series row, whose `date` is the series START — so "delete only
  // this occurrence" would always hit the first occurrence.
  const [viewingOccurrenceDate, setViewingOccurrenceDate] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  // The current user's own profile (display name + avatar). Loaded once
  // when the component mounts, so avatars on the user's own events render
  // correctly even outside shared calendars where membersById is empty.
  const [myProfile, setMyProfile] = useState(null);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    loadProfile()
      .then((p) => {
        if (!cancelled) setMyProfile(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Resolve a userId → profile-like object. Falls back to the cached
  // current-user profile when the shared-calendar member map doesn't have
  // an entry (e.g. personal calendar, or the user opened the page before
  // the member list finished loading).
  const lookupMember = (uid) => {
    if (membersById[uid]) return membersById[uid];
    if (user && uid === user.id && myProfile) {
      return {
        userId: user.id,
        displayName: myProfile.displayName,
        avatarEmoji: myProfile.avatarEmoji,
        avatarColor: myProfile.avatarColor,
        avatarUrl: myProfile.avatarUrl,
        avatarKind: myProfile.avatarKind
      };
    }
    return null;
  };

  // Render a tiny circular avatar for an event's creator (shared view only).
  const renderCreatorAvatar = (uid, size = 22) => {
    if (!uid) return null;
    return renderMemberAvatar(lookupMember(uid), size);
  };

  // Always start on the calendar grid when the view changes.
  useEffect(() => {
    setShowSharedBirthdays(false);
  }, [sharedCalendarId]);

  useEffect(() => {
    if (!sharedCalendarId) {
      setMembersById({});
      return;
    }
    let cancelled = false;
    listSharedCalendarMembers(sharedCalendarId)
      .then((members) => {
        if (cancelled) return;
        const map = {};
        for (const m of members) map[m.userId] = m;
        setMembersById(map);
      })
      .catch(() => {
        if (!cancelled) setMembersById({});
      });
    return () => {
      cancelled = true;
    };
  }, [sharedCalendarId]);

  const sharedCalendars = data.calendar?.sharedCalendars || [];
  const currentSharedCalendar = sharedCalendarId
    ? sharedCalendars.find((c) => c.id === sharedCalendarId) || null
    : null;
  const isSharedView = !!sharedCalendarId;

  // Reminders + daily digest of this shared calendar: one setting for every
  // member, saved on the calendar row. Optimistic locally; Realtime brings the
  // same change to the other members.
  const saveSharedNotifications = async (patch) => {
    if (!currentSharedCalendar) return;
    const next = {
      digestTime: currentSharedCalendar.digestTime || null,
      reminderMinutes: currentSharedCalendar.reminderMinutes || null,
      birthdayReminderDays: currentSharedCalendar.birthdayReminderDays || null,
      ...patch,
      tz: deviceTimeZone()
    };
    try {
      await setSharedCalendarNotifications(sharedCalendarId, next);
      updateData({
        calendar: {
          ...data.calendar,
          sharedCalendars: sharedCalendars.map((c) =>
            c.id === sharedCalendarId ? { ...c, ...next } : c
          )
        }
      });
    } catch (err) {
      toast.error(err.message || t('common.error'));
    }
  };
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    startTime: '',
    endTime: '',
    allDay: false,
    categoryId: '',
    notes: '',
    recurrence: null,
    linkedSportName: null,
    participantIds: []
  });

  const allCalendarEvents = data.calendar?.events || [];
  const calendarEvents = useMemo(() => {
    if (isSharedView) {
      return allCalendarEvents.filter((ev) => ev.sharedCalendarId === sharedCalendarId);
    }
    return allCalendarEvents.filter((ev) => !ev.sharedCalendarId);
  }, [allCalendarEvents, isSharedView, sharedCalendarId]);
  const otherCalendarEvents = useMemo(() => {
    if (isSharedView) {
      return allCalendarEvents.filter((ev) => ev.sharedCalendarId !== sharedCalendarId);
    }
    return allCalendarEvents.filter((ev) => !!ev.sharedCalendarId);
  }, [allCalendarEvents, isSharedView, sharedCalendarId]);
  // Activity feed: aggregate every audit entry + chat comment from every
  // event the user can currently see, grouped by event and sorted with the
  // most-recently-active event first. Used by the bottom "Activity" tab to
  // show "what changed since I last looked" without opening every event.
  const activityFeed = useMemo(
    () => buildActivityFeed(allCalendarEvents, data.calendar?.sharedCalendars || []),
    [allCalendarEvents, data.calendar?.sharedCalendars]
  );

  // Persist a new value for the *current* calendar's events while keeping
  // events that belong to other (personal/shared) buckets untouched.
  const commitEvents = (list) => {
    updateData({
      calendar: { ...data.calendar, events: [...otherCalendarEvents, ...list] }
    });
  };
  // Persist a new value for the FULL cross-calendar event list. Needed by
  // the detail view, which can show events from any calendar (opened via
  // the activity feed) — mapping over the scoped list there would silently
  // discard comments/edits/deletes for out-of-scope events.
  const commitAllEvents = (list) => {
    updateData({ calendar: { ...data.calendar, events: list } });
  };
  // All category rows across every calendar the user can see. Each row has
  // `sharedCalendarId` (null for personal, set for shared). The UI scopes
  // labels to the current calendar — labels edited in one calendar don't
  // affect another, but shared-calendar edits are visible to every member.
  const allCategories = data.calendar?.categories || [];

  // Rows that belong to the current view (personal vs one shared calendar).
  const scopedCategories = useMemo(
    () =>
      allCategories.filter((c) =>
        isSharedView
          ? c.sharedCalendarId === sharedCalendarId
          : !c.sharedCalendarId
      ),
    [allCategories, isSharedView, sharedCalendarId]
  );

  // For shared calendars we don't persist the 13 default color rows. Make
  // sure every default is present so the label picker always shows them,
  // overlaid with any custom renames from `scopedCategories`.
  const categories = useMemo(() => {
    if (!isSharedView) return scopedCategories;
    const defaults = defaultData.calendar.categories;
    const byId = new Map(scopedCategories.map((c) => [c.id, c]));
    return defaults.map((d) => byId.get(d.id) || { ...d, sharedCalendarId });
  }, [scopedCategories, isSharedView, sharedCalendarId]);

  // Birthday settings for the current calendar. In a shared view we use the
  // shared calendar's own columns; on the personal calendar we use the
  // global `birthdaySettings` singleton.
  // Austrian public holidays, off by default. The chosen label is reserved
  // for them (see displayCategories), like the birthday label. A shared
  // calendar keeps the setting on its own row (every member sees the same);
  // the personal calendar keeps it in calendar_settings.
  const personalHolidaySettings = data.calendar?.holidaySettings || { enabled: false, categoryId: 'red' };
  const holidaySettings =
    isSharedView && currentSharedCalendar
      ? {
          enabled: !!currentSharedCalendar.holidaysEnabled,
          categoryId: currentSharedCalendar.holidayCategoryId || 'red'
        }
      : personalHolidaySettings;
  // Lives here rather than in LabelManager so it survives closing the sheet.
  const [showHolidayColorPicker, setShowHolidayColorPicker] = useState(false);
  const updateHolidaySettings = async (patch) => {
    if (isSharedView && currentSharedCalendar) {
      const next = { ...holidaySettings, ...patch };
      try {
        await setSharedCalendarHolidays(sharedCalendarId, next);
        updateData({
          calendar: {
            ...data.calendar,
            sharedCalendars: sharedCalendars.map((c) =>
              c.id === sharedCalendarId
                ? { ...c, holidaysEnabled: next.enabled, holidayCategoryId: next.categoryId }
                : c
            )
          }
        });
      } catch (err) {
        toast.error(err.message || t('common.error'));
      }
      return;
    }
    updateData({
      calendar: { ...(data.calendar || {}), holidaySettings: { ...personalHolidaySettings, ...patch } }
    });
  };

  const personalBirthdaySettings = data.calendar?.birthdaySettings || {
    linkedToCalendar: false,
    categoryId: 'purple'
  };
  const birthdaySettings = useMemo(() => {
    if (isSharedView && currentSharedCalendar) {
      return {
        linkedToCalendar:
          currentSharedCalendar.hasBirthdays &&
          currentSharedCalendar.birthdayLinked !== false,
        categoryId: currentSharedCalendar.birthdayCategoryId || 'purple',
        hasBirthdays: !!currentSharedCalendar.hasBirthdays
      };
    }
    return { ...personalBirthdaySettings, hasBirthdays: true };
  }, [
    isSharedView,
    currentSharedCalendar,
    personalBirthdaySettings
  ]);

  // Only consider birthdays belonging to the current calendar scope.
  const allBirthdays = data.calendar?.birthdays || [];
  const rawBirthdays = useMemo(
    () =>
      allBirthdays.filter((b) =>
        isSharedView
          ? b.sharedCalendarId === sharedCalendarId
          : !b.sharedCalendarId
      ),
    [allBirthdays, isSharedView, sharedCalendarId]
  );

  const birthdayEvents = useMemo(
    () =>
      buildBirthdayEvents({
        rawBirthdays,
        linkedToCalendar: birthdaySettings.linkedToCalendar,
        categoryId: birthdaySettings.categoryId,
        hasBirthdays: birthdaySettings.hasBirthdays,
        isSharedView,
        currentDate
      }),
    [
      rawBirthdays,
      birthdaySettings.linkedToCalendar,
      birthdaySettings.categoryId,
      birthdaySettings.hasBirthdays,
      isSharedView,
      currentDate
    ]
  );

  // Gesetzliche Feiertage as read-only all-day events, generated for every
  // year the 3-month carousel can touch (same reasoning as birthdays).
  const holidayEvents = useMemo(
    () =>
      buildHolidayEvents({
        enabled: holidaySettings.enabled,
        categoryId: holidaySettings.categoryId,
        currentDate,
        lang
      }),
    [isSharedView, holidaySettings.enabled, holidaySettings.categoryId, currentDate, lang]
  );

  // To-dos linked into the calendar the user chose (personal or one shared
  // calendar). Same pattern as birthdays: the events are derived from the
  // task rows rather than stored, so ticking a task off on the Todo page
  // immediately crosses it off here too. In a shared view we additionally
  // render other members' tasks shared into this calendar (read-only rows
  // the loader put into data.todo.sharedTasks).
  const todoCalendarSettings = data.todo?.calendarSettings || {
    linkedToCalendar: false,
    categoryId: 'blue',
    sharedCalendarId: null
  };
  const todoTasks = data.todo?.tasks || [];
  const sharedTodoTasks = data.todo?.sharedTasks || [];
  const todoEvents = useMemo(
    () =>
      buildTodoEvents({
        todoTasks,
        sharedTodoTasks,
        linkedToCalendar: todoCalendarSettings.linkedToCalendar,
        categoryId: todoCalendarSettings.categoryId,
        todoSharedCalendarId: todoCalendarSettings.sharedCalendarId,
        isSharedView,
        sharedCalendarId
      }),
    [
      todoTasks,
      sharedTodoTasks,
      todoCalendarSettings.linkedToCalendar,
      todoCalendarSettings.categoryId,
      todoCalendarSettings.sharedCalendarId,
      isSharedView,
      sharedCalendarId
    ]
  );

  // Which label colour each member's to-dos occupy in THIS calendar. Used to
  // show the colour as taken ("Janas To-dos … aus To-dos (Jana)"), the same
  // way the birthday label is reserved.
  const todoLabelOwners = useMemo(
    () =>
      buildTodoLabelOwners({
        ownTasks: data.todo?.tasks || [],
        foreignTasks: data.todo?.sharedTasks || [],
        settings: todoCalendarSettings,
        isSharedView,
        sharedCalendarId,
        userId: user?.id || null
      }),
    [
      data.todo?.tasks,
      data.todo?.sharedTasks,
      todoCalendarSettings,
      isSharedView,
      sharedCalendarId,
      user
    ]
  );

  // events is computed after calendarDays (see below).

  // Apply birthday-label override: if a category is linked as the birthday label,
  // display it as "Birthday" with filled color (treat as a used/custom label).
  // Same for the todo label; if both link the same color, birthday wins the
  // name (todo events still render in the shared color).
  const displayCategories = useMemo(
    () =>
      applyLabelOverrides(categories, {
        birthdayLinked: birthdaySettings.linkedToCalendar,
        birthdayCategoryId: birthdaySettings.categoryId,
        holidaysEnabled: holidaySettings.enabled,
        holidayCategoryId: holidaySettings.categoryId,
        todoLabelOwners,
        membersById,
        myProfile,
        user,
        t
      }),
    [
      categories,
      birthdaySettings.linkedToCalendar,
      birthdaySettings.categoryId,
      holidaySettings.enabled,
      holidaySettings.categoryId,
      isSharedView,
      todoLabelOwners,
      membersById,
      myProfile,
      user,
      t
    ]
  );

  // Custom labels float to the top, defaults stay in rainbow order
  const sortedCategories = useMemo(() => {
    const custom = displayCategories.filter((c) => c.isCustom);
    const defaults = displayCategories.filter((c) => !c.isCustom);
    return [...custom, ...defaults];
  }, [displayCategories]);

  // Categories shown in the event-form dropdown. The Birthday- and
  // Todo-linked labels are reserved for entries created from their pages,
  // so we hide them from manual event creation.
  const selectableCategories = useMemo(
    () => sortedCategories.filter((c) => !c.isBirthdayLabel && !c.isTodoLabel && !c.isHolidayLabel),
    [sortedCategories]
  );

  // Default label for new events. Resolution order:
  //   1. In a shared calendar, the current user's per-calendar preference
  //      (set via the members panel) wins, so each member can have their
  //      own colour.
  //   2. Otherwise, the label of the *current user's* most recently saved
  //      event in this calendar (NOT global, so other members' labels
  //      don't bleed into yours).
  //   3. Fall back to the first selectable label.
  const myMemberCategoryId = useMemo(() => {
    if (!isSharedView || !user) return null;
    const me = membersById[user.id];
    if (!me || !me.categoryId) return null;
    if (!selectableCategories.some((c) => c.id === me.categoryId)) return null;
    return me.categoryId;
  }, [isSharedView, user, membersById, selectableCategories]);

  const lastUsedCategoryId = useMemo(() => {
    if (myMemberCategoryId) return myMemberCategoryId;
    const myId = user?.id || null;
    const sorted = [...calendarEvents].sort((a, b) =>
      String(b.id).localeCompare(String(a.id))
    );
    const recent = sorted.find(
      (ev) =>
        ev.categoryId &&
        // Only consider this user's own events. In a personal calendar
        // every event is theirs (createdBy may be null on legacy rows,
        // which we still accept). In a shared calendar we strictly require
        // a matching createdBy so other members' choices don't show up as
        // *your* default.
        (isSharedView ? ev.createdBy === myId : !ev.createdBy || ev.createdBy === myId) &&
        selectableCategories.some((c) => c.id === ev.categoryId)
    );
    return recent?.categoryId || selectableCategories[0]?.id || '';
  }, [calendarEvents, selectableCategories, user, isSharedView, myMemberCategoryId]);

  const getCategory = (id) => displayCategories.find((c) => c.id === id) || displayCategories[0];

  const updateLabel = (id, name) => {
    const trimmed = name.trim();
    const scope = sharedCalendarId || null;
    const isCurrentScope = (c) => (c.sharedCalendarId || null) === scope;

    // Partition: current-scope rows get modified, everything else passes
    // through untouched so other calendars' labels aren't affected.
    const otherScope = allCategories.filter((c) => !isCurrentScope(c));
    const currentScope = allCategories.filter(isCurrentScope);
    const existing = currentScope.find((c) => c.id === id);

    let nextCurrent;
    if (!trimmed) {
      if (scope && existing) {
        // Shared view: clearing the name removes the row so the default
        // label (from `defaultData`) takes over for every member again.
        nextCurrent = currentScope.filter((c) => c.id !== id);
      } else if (!scope && existing) {
        const capitalizedId = id.charAt(0).toUpperCase() + id.slice(1);
        nextCurrent = currentScope.map((c) =>
          c.id === id ? { ...c, name: capitalizedId, isCustom: false } : c
        );
      } else {
        nextCurrent = currentScope;
      }
    } else if (existing) {
      nextCurrent = currentScope.map((c) =>
        c.id === id ? { ...c, name: trimmed, isCustom: true } : c
      );
    } else {
      // No row in this scope yet (shared view): create one using the default
      // color for this id as the template.
      const base =
        defaultData.calendar.categories.find((d) => d.id === id) || { id };
      nextCurrent = [
        ...currentScope,
        { ...base, name: trimmed, isCustom: true, sharedCalendarId: scope }
      ];
    }

    updateData({
      calendar: {
        ...data.calendar,
        categories: [...otherScope, ...nextCurrent]
      }
    });
  };

  // Expand recurring events into concrete instances for a 3-month window
  // (prev/current/next) so the carousel swipe can reveal real data on the
  // adjacent months instead of empty grids.
  const expandedCalendarEvents = useMemo(() => {
    const prevStart = startOfWeek(startOfMonth(subMonths(currentDate, 1)), { weekStartsOn: 1 });
    const nextEnd = endOfWeek(endOfMonth(addMonths(currentDate, 1)), { weekStartsOn: 1 });
    return expandEvents(calendarEvents, addDays(prevStart, -7), addDays(nextEnd, 7));
  }, [calendarEvents, currentDate]);

  const events = useMemo(
    () => [...expandedCalendarEvents, ...birthdayEvents, ...holidayEvents, ...todoEvents],
    [expandedCalendarEvents, birthdayEvents, holidayEvents, todoEvents]
  );

  // What a new or edited series row needs to know: which calendar it belongs
  // to and who made the change.
  const seriesCtx = { sharedCalendarId, userId: user?.id || null };

  // Apply `eventData` to the event (or whole series) with id `baseId`.
  // Maps over ALL events — the edit modal can be reached from the detail view
  // of an event outside the current calendar scope (activity feed); mapping
  // the scoped list would silently drop the edit.
  const applyEditToSeries = (baseId, eventData) => {
    commitAllEvents(editWholeSeries(allCalendarEvents, baseId, eventData, seriesCtx));
  };

  // --- Editing one occurrence, or from one occurrence on ------------------
  // The list transforms live in calendar/seriesEdits.js; these resolve the
  // series row, commit the result and close the chooser.
  const finishSeriesEdit = () => {
    setRecurringEditTarget(null);
    closeModal();
  };

  const editOnlyThisOccurrence = () => {
    const { baseId, occurrenceDate, eventData } = recurringEditTarget;
    const base = allCalendarEvents.find((e) => e.id === baseId);
    if (!base) return finishSeriesEdit();
    commitAllEvents(editOccurrenceOnly(allCalendarEvents, base, occurrenceDate, eventData, seriesCtx));
    finishSeriesEdit();
  };

  const editThisAndFuture = () => {
    const { baseId, occurrenceDate, eventData } = recurringEditTarget;
    const base = allCalendarEvents.find((e) => e.id === baseId);
    if (!base) return finishSeriesEdit();
    commitAllEvents(editFromOccurrenceOn(allCalendarEvents, base, occurrenceDate, eventData, seriesCtx));
    finishSeriesEdit();
  };

  const editEntireSeries = () => {
    const { baseId, eventData } = recurringEditTarget;
    applyEditToSeries(baseId, eventData);
    finishSeriesEdit();
  };

  const handleSaveEvent = () => {
    if (!newEvent.title.trim() || !newEvent.date) return;
    const eventData = {
      ...newEvent,
      title: newEvent.title.trim(),
      endDate: newEvent.endDate || newEvent.date,
      recurrence: newEvent.recurrence || null,
      linkedSportName: isSharedView ? null : (newEvent.linkedSportName || null),
      participantIds: isSharedView ? (newEvent.participantIds || []) : [],
      sharedCalendarId: sharedCalendarId || null
    };

    const now = new Date().toISOString();
    const me = user?.id || null;
    if (editingEvent) {
      const baseId = editingEvent.originalId || editingEvent.id;
      const base = allCalendarEvents.find((ev) => ev.id === baseId);
      if (base && base.recurrence && base.recurrence.freq) {
        // A series: ask which occurrences the change applies to. The three
        // handlers below finish the save.
        setRecurringEditTarget({
          baseId,
          occurrenceDate: editingEvent.clickedOccurrenceDate || base.date,
          eventData
        });
        return;
      }
      applyEditToSeries(baseId, eventData);
    } else {
      commitEvents([
        ...calendarEvents,
        {
          id: Date.now().toString(),
          ...eventData,
          audit: [
            {
              id: `${now}-create`,
              type: 'created',
              userId: me,
              at: now
            }
          ],
          comments: []
        }
      ]);
    }
    closeModal();
  };

  // Append a chat comment to the currently-viewed event.
  const handleAddComment = () => {
    const text = commentDraft.trim();
    if (!text || !viewingEventId) return;
    const me = user?.id || null;
    const now = new Date().toISOString();
    // Map over ALL events, not the scoped list — the detail view can show
    // an event from another calendar (via the activity feed).
    const updated = allCalendarEvents.map((ev) => {
      if (ev.id !== viewingEventId) return ev;
      const comments = Array.isArray(ev.comments) ? ev.comments : [];
      return {
        ...ev,
        comments: [
          ...comments,
          {
            id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
            userId: me,
            text,
            at: now
          }
        ]
      };
    });
    commitAllEvents(updated);
    setCommentDraft('');
  };

  const handleDeleteEvent = async (id) => {
    const baseId = (editingEvent && editingEvent.originalId) || id;
    const target = allCalendarEvents.find((e) => e.id === baseId);
    const isSeries = target && target.recurrence && target.recurrence.freq;
    if (isSeries) {
      // Open 3-option dialog
      const occurrenceDate =
        (editingEvent && editingEvent.clickedOccurrenceDate) || target.date;
      setRecurringDeleteTarget({ baseId, occurrenceDate });
      return;
    }
    if (!(await confirm({ message: t('calendar.deleteEventConfirm'), danger: true }))) return;
    commitAllEvents(allCalendarEvents.filter((ev) => ev.id !== baseId));
    closeModal();
  };

  // Recurring deletion handlers. These operate on the full event list so
  // they also work for events opened from outside the current calendar
  // scope (activity feed → detail view → delete).
  const deleteOnlyThisOccurrence = () => {
    const { baseId, occurrenceDate } = recurringDeleteTarget;
    const target = allCalendarEvents.find((e) => e.id === baseId);
    if (!target) return;
    commitAllEvents(deleteOccurrenceOnly(allCalendarEvents, target, occurrenceDate));
    setRecurringDeleteTarget(null);
    closeModal();
  };

  const deleteThisAndFuture = () => {
    const { baseId, occurrenceDate } = recurringDeleteTarget;
    const target = allCalendarEvents.find((e) => e.id === baseId);
    if (!target) return;
    commitAllEvents(deleteFromOccurrenceOn(allCalendarEvents, target, occurrenceDate));
    setRecurringDeleteTarget(null);
    closeModal();
  };

  const deleteEntireSeries = () => {
    const { baseId } = recurringDeleteTarget;
    commitAllEvents(allCalendarEvents.filter((e) => e.id !== baseId));
    setRecurringDeleteTarget(null);
    closeModal();
  };

  const closeModal = () => {
    setShowAddEvent(false);
    setEditingEvent(null);
    setNewEvent({
      title: '',
      date: format(selectedDate || new Date(), 'yyyy-MM-dd'),
      endDate: '',
      startTime: '',
      endTime: '',
      allDay: false,
      categoryId: lastUsedCategoryId,
      notes: '',
      recurrence: null,
      linkedSportName: null,
      participantIds: isSharedView && user ? [user.id] : []
    });
  };

  const openAddEvent = (date) => {
    setSelectedDate(date);
    setNewEvent({
      title: '',
      date: format(date, 'yyyy-MM-dd'),
      endDate: '',
      startTime: '',
      endTime: '',
      allDay: false,
      categoryId: lastUsedCategoryId,
      notes: '',
      recurrence: null,
      linkedSportName: null,
      participantIds: isSharedView && user ? [user.id] : []
    });
    setShowAddEvent(true);
  };

  // `occurrenceDate` carries the tapped occurrence when the caller only has
  // the base series row (detail view) — without it, `event.date` would be
  // the series START and occurrence-scoped deletes would hit the wrong one.
  const openEditEvent = (event, occurrenceDate = null) => {
    // If clicking an expanded occurrence, edit the base series
    const baseId = event.originalId || event.id;
    const base = allCalendarEvents.find((e) => e.id === baseId) || event;
    setEditingEvent({
      ...base,
      originalId: baseId,
      // the occurrence the user actually clicked
      clickedOccurrenceDate: occurrenceDate || event.date
    });
    setNewEvent({
      title: base.title,
      date: base.date,
      endDate: base.endDate || base.date,
      startTime: base.startTime || '',
      endTime: base.endTime || '',
      allDay: base.allDay !== false,
      categoryId: base.categoryId || lastUsedCategoryId,
      notes: base.notes || '',
      recurrence: base.recurrence || null,
      linkedSportName: base.linkedSportName || null,
      participantIds: Array.isArray(base.participantIds)
        ? base.participantIds
        : base.createdBy
        ? [base.createdBy]
        : []
    });
    setShowAddEvent(true);
  };

  // Phones: first tap selects the day, tapping the already-selected day
  // opens the sheet above the calendar (a sheet on the first tap would hide
  // the grid before you have seen which day you hit). md+: the panel is a
  // column beside the grid, hides nothing, so one tap selects and opens.
  const handleDayClick = (day) => {
    if (isWide) {
      setSelectedDate(day);
      setDayPanelDate(day);
      return;
    }
    if (selectedDate && isSameDay(day, selectedDate)) {
      setDayPanelDate(day);
    } else {
      setSelectedDate(day);
      setDayPanelDate(null);
    }
  };

  // Tapping an entry in the day panel opens the detail view (read-only, with
  // comments and history). Remember WHICH occurrence was tapped — for a
  // recurring instance `ev.date` is the occurrence date while the base row's
  // date is the series start.
  const openEventFromDay = (ev) => {
    setViewingEventId(ev.originalId || ev.id);
    setViewingOccurrenceDate(ev.originalId ? ev.date : null);
    setDayPanelDate(null);
  };
  const openEventFromFeed = (ev) => {
    setViewingEventId(ev.id);
    setViewingOccurrenceDate(null);
  };

  // Acting on a widget tap: jump to the month, optionally open that day's
  // panel or the new-event form. Keyed on the intent's nonce so tapping the
  // same day twice works.
  const intentNonceRef = useRef(null);
  useEffect(() => {
    if (!intent || intent.nonce === intentNonceRef.current) return;
    intentNonceRef.current = intent.nonce;
    const value = intent.value || '';
    const day = /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISO(value) : null;
    const month = /^\d{4}-\d{2}$/.test(value) ? parseISO(`${value}-01`) : null;
    const target = day || month || new Date();
    setCurrentDate(target);
    if (intent.action === 'day' && day) {
      setSelectedDate(day);
      setDayPanelDate(day);
    } else if (intent.action === 'new') {
      setSelectedDate(day || target);
      openAddEvent(day || target);
    } else {
      setSelectedDate(null);
      setDayPanelDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);

  // Slide the carousel to `target` (any month), the same animation a swipe
  // or the tab's jump-to-today uses. Desktop has no swipe, so the header's
  // ‹ › and « » buttons go through here.
  const slideToMonth = (target) => {
    if (swipeAnim) return; // let the current slide finish
    const backwards = target < currentDate;
    const width = carouselRef.current?.offsetWidth || window.innerWidth;
    setSelectedDate(null);
    setDayPanelDate(null);
    setJumpTo(target);
    setJumpBack(backwards);
    setSwipeAnim(true);
    setSwipeDx(backwards ? width : -width);
    setTimeout(() => {
      setCurrentDate(target);
      setSwipeAnim(false);
      setSwipeDx(0);
      setJumpTo(null);
    }, 240);
  };

  // Month/year lists for the wheel-style picker.
  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, m) => fmt(new Date(2000, m, 1), 'month')),
    [lang]
  );
  const yearOptions = useMemo(() => {
    const base = getYear(new Date());
    const out = [];
    for (let y = base - 20; y <= base + 20; y++) out.push(y);
    return out;
  }, []);

  // Same source as the home-screen widget, so the two can't drift apart.
  const weekdayLabels = useMemo(() => localeWeekdays(), [lang]);

  // The gear button: load the member list + invite link, then open the sheet.
  const openSharedSettings = async () => {
    setSharedSettings({ loading: true, members: [], inviteUrl: null, copied: false });
    try {
      const [members, inviteUrl] = await Promise.all([
        listSharedCalendarMembers(sharedCalendarId),
        currentSharedCalendar.isOwner
          ? getInviteUrl(sharedCalendarId)
          : Promise.resolve(null)
      ]);
      setSharedSettings({ loading: false, members, inviteUrl, copied: false });
      // Refresh the avatar/category lookup map too so the
      // member-color dots update immediately after they were
      // changed in another session.
      const map = {};
      for (const m of members) map[m.userId] = m;
      setMembersById(map);
    } catch (err) {
      setSharedSettings(null);
      window.alert(err.message || t('calendar.loadSettingsFailed'));
    }
  };

  // Shared-calendar birthday sub-view: reuse the standalone BirthdayCalendar
  // with a `sharedCalendarId` so reads/writes are scoped to this calendar.
  if (
    showSharedBirthdays &&
    isSharedView &&
    currentSharedCalendar &&
    currentSharedCalendar.hasBirthdays
  ) {
    return (
      <BirthdayCalendar
        data={data}
        updateData={updateData}
        sharedCalendarId={sharedCalendarId}
        onBack={() => setShowSharedBirthdays(false)}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Header — month picker + actions on a single line, no wrap.
          Hidden when the user has switched the bottom toggle to "Activity"
          so the feed has the full screen. */}
      {viewMode === 'calendar' && (
        <CalendarHeader
          currentDate={currentDate}
          showMonthPicker={showMonthPicker}
          setShowMonthPicker={setShowMonthPicker}
          monthLabels={monthLabels}
          yearOptions={yearOptions}
          onChangeMonthYear={(m, y) => {
            setCurrentDate((d) => setYear(setMonth(d, m), y));
          }}
          onSlideTo={slideToMonth}
          isSharedView={isSharedView}
          currentSharedCalendar={currentSharedCalendar}
          onCreateShared={() => {
            setNewSharedName('');
            setShowCreateShared(true);
          }}
          onShowBirthdays={() => setShowSharedBirthdays(true)}
          onOpenSettings={openSharedSettings}
          onOpenLabelManager={() => setShowLabelManager(true)}
          onAddEvent={() => openAddEvent(selectedDate || currentDate)}
        />
      )}

      {/* Create shared calendar modal */}
      {showCreateShared && (
        <CreateSharedCalendarModal
          newSharedName={newSharedName}
          setNewSharedName={setNewSharedName}
          newSharedWithBirthdays={newSharedWithBirthdays}
          setNewSharedWithBirthdays={setNewSharedWithBirthdays}
          creatingShared={creatingShared}
          setCreatingShared={setCreatingShared}
          onClose={() => setShowCreateShared(false)}
          reload={reload}
        />
      )}

      {/* Shared calendar settings modal */}
      {sharedSettings && currentSharedCalendar && (
        <SharedCalendarSheet
          sharedSettings={sharedSettings}
          setSharedSettings={setSharedSettings}
          currentSharedCalendar={currentSharedCalendar}
          sharedCalendarId={sharedCalendarId}
          selectableCategories={selectableCategories}
          categories={categories}
          membersById={membersById}
          setMembersById={setMembersById}
          holidaySettings={holidaySettings}
          updateHolidaySettings={updateHolidaySettings}
          birthdaySettings={birthdaySettings}
          saveSharedNotifications={saveSharedNotifications}
          reload={reload}
        />
      )}

      {/* Day events sheet (below md) — slides up from the bottom, full
          viewport height with a narrow width (centered horizontally), so it
          covers the calendar without going edge-to-edge. From md the same
          panel is rendered inline beside the grid instead (below). */}
      {dayPanelDate && !isWide && (
        <DayPanel
          date={dayPanelDate}
          items={eventsForDay(events, dayPanelDate)}
          getCategory={getCategory}
          isSharedView={isSharedView}
          renderCreatorAvatar={renderCreatorAvatar}
          onClose={() => setDayPanelDate(null)}
          onAddEvent={openAddEvent}
          onOpenEvent={openEventFromDay}
        />
      )}

      {/* Calendar Grid — edge-to-edge on mobile so each day column gets as
          much horizontal space as possible. On sm+ the usual rounded card
          style returns. The inner strip is a 3-wide carousel (prev /
          current / next) so horizontal swipes smoothly reveal the adjacent
          months under the finger. From md the grid and the day column share
          a row: the grid takes what the column leaves. */}
      {viewMode === 'calendar' && (
      <div className="md:flex md:gap-3">
      <div
        ref={carouselRef}
        className="bg-white border-y sm:border sm:rounded-xl border-gray-200 overflow-hidden -mx-4 sm:mx-0 touch-pan-y relative md:flex-1 md:min-w-0"
        onTouchStart={(e) => {
          const t = e.touches[0];
          const width = carouselRef.current?.offsetWidth || window.innerWidth;
          swipeRef.current = { x: t.clientX, y: t.clientY, width, locked: false };
          setSwipeAnim(false);
        }}
        onTouchMove={(e) => {
          const s = swipeRef.current;
          if (!s) return;
          const t = e.touches[0];
          const dx = t.clientX - s.x;
          const dy = t.clientY - s.y;
          // Lock direction on first significant movement. Only horizontal
          // drags drive the carousel; vertical ones let the page scroll.
          if (!s.locked) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            s.locked = Math.abs(dx) > Math.abs(dy);
          }
          if (s.locked) {
            setSwipeActive(true);
            setSwipeDx(dx);
          }
        }}
        onTouchEnd={(e) => {
          const s = swipeRef.current;
          swipeRef.current = null;
          if (!s || !s.locked) {
            setSwipeActive(false);
            setSwipeDx(0);
            return;
          }
          const dx = swipeDx;
          const width = s.width || 1;
          const threshold = Math.min(80, width * 0.2);
          if (Math.abs(dx) > threshold) {
            // Animate to neighbour, then commit and reset with no transition.
            setSwipeAnim(true);
            setSwipeDx(dx < 0 ? -width : width);
            setTimeout(() => {
              setCurrentDate((d) => (dx < 0 ? addMonths(d, 1) : subMonths(d, 1)));
              setSwipeAnim(false);
              setSwipeActive(false);
              setSwipeDx(0);
            }, 220);
          } else {
            // Snap back.
            setSwipeAnim(true);
            setSwipeDx(0);
            setTimeout(() => {
              setSwipeAnim(false);
              setSwipeActive(false);
            }, 200);
          }
        }}
      >
        {(() => {
          const renderMonthGrid = (baseDate) => (
            <MonthGrid
              baseDate={baseDate}
              events={events}
              compact={compactGrid}
              weekdayLabels={weekdayLabels}
              getCategory={getCategory}
              selectedDate={selectedDate}
              popToday={popToday}
              onDayClick={handleDayClick}
            />
          );
          const prev = jumpTo && jumpBack ? jumpTo : subMonths(currentDate, 1);
          const next = jumpTo && !jumpBack ? jumpTo : addMonths(currentDate, 1);
          // Always render the 3-wide strip so the very first swipe doesn't
          // remount the grid mid-touch (which would drop the gesture).
          return (
            <div
              className="flex"
              style={{
                width: '300%',
                transform: `translateX(calc(-33.3333% + ${swipeDx}px))`,
                transition: swipeAnim ? 'transform 220ms ease-out' : 'none',
                willChange: 'transform'
              }}
            >
              <div className="w-1/3 flex-shrink-0">{renderMonthGrid(prev)}</div>
              <div className="w-1/3 flex-shrink-0">{renderMonthGrid(currentDate)}</div>
              <div className="w-1/3 flex-shrink-0">{renderMonthGrid(next)}</div>
            </div>
          );
        })()}
      </div>
      {/* Day column (md+) — the selected day stays ringed in the grid
          while it is open. */}
      {dayPanelDate && isWide && (
        <DayPanel
          inline
          date={dayPanelDate}
          items={eventsForDay(events, dayPanelDate)}
          getCategory={getCategory}
          isSharedView={isSharedView}
          renderCreatorAvatar={renderCreatorAvatar}
          onClose={() => setDayPanelDate(null)}
          onAddEvent={openAddEvent}
          onOpenEvent={openEventFromDay}
        />
      )}
      </div>
      )}

      {/* Activity feed — chronological list of every recent change/comment
          across the events the user can see. Rendered when the bottom
          toggle is set to "Activity". */}
      {viewMode === 'activity' && (
        <ActivityFeed
          feed={activityFeed}
          getCategory={getCategory}
          renderCreatorAvatar={renderCreatorAvatar}
          onOpenEvent={openEventFromFeed}
        />
      )}

      {/* Bottom segmented toggle — Calendar | Activity. Fixed to the
          bottom of the viewport (above the iOS home indicator) so it's
          always reachable without stretching. Only relevant in the
          standalone (non-shared, non-birthday) calendar context. */}
      <div className="fixed above-tabbar left-1/2 -translate-x-1/2 z-30 px-2">
        <div className="bg-gray-900/95 dark:bg-gray-800/95 backdrop-blur rounded-full shadow-lg flex p-1 gap-1">
          <button
            type="button"
            onClick={() => setViewMode('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              viewMode === 'calendar'
                ? 'bg-white text-gray-900'
                : 'text-gray-300 hover:text-white'
            }`}
            aria-label={t('calendar.calendarView')}
          >
            <CalendarDays size={16} />
            <span>{t('nav.calendar')}</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode('activity')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              viewMode === 'activity'
                ? 'bg-white text-gray-900'
                : 'text-gray-300 hover:text-white'
            }`}
            aria-label={t('calendar.activity')}
          >
            <Bell size={16} />
            <span>{t('calendar.activity')}</span>
          </button>
        </div>
      </div>

      {/* Floating Add-event button — mobile only. On desktop the inline
          "Event" button in the header is sufficient. */}
      {viewMode === 'calendar' && (
      <button
        onClick={() => openAddEvent(selectedDate || currentDate)}
        className="sm:hidden fixed right-5 z-30 h-14 w-14 rounded-full shadow-lg bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-transform"
        // Stacked above the Kalender/Aktivität switcher, which itself sits
        // above the tab bar.
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 9.25rem)' }}
        aria-label={t('calendar.newEvent')}
      >
        <Plus size={26} />
      </button>
      )}

      {/* Add/Edit Event Modal */}
      {showAddEvent && (
        <EventForm
          editingEvent={editingEvent}
          newEvent={newEvent}
          setNewEvent={setNewEvent}
          isSharedView={isSharedView}
          membersById={membersById}
          renderCreatorAvatar={renderCreatorAvatar}
          calendarEvents={calendarEvents}
          getCategory={getCategory}
          selectableCategories={selectableCategories}
          sports={data.fitness?.sports || []}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          onClose={closeModal}
        />
      )}

      {/* Event Detail View — opens when an event in the day list is tapped.
          Shows the event in its label color with participant avatars,
          date/time, an activity log, and chat-style comments. */}
      {viewingEventId && (
        <EventDetail
          eventId={viewingEventId}
          occurrenceDate={viewingOccurrenceDate}
          events={allCalendarEvents}
          getCategory={getCategory}
          membersById={membersById}
          renderCreatorAvatar={renderCreatorAvatar}
          onClose={() => setViewingEventId(null)}
          onEdit={openEditEvent}
          onRequestRecurringDelete={setRecurringDeleteTarget}
          commitAllEvents={commitAllEvents}
          commentDraft={commentDraft}
          setCommentDraft={setCommentDraft}
          onAddComment={handleAddComment}
        />
      )}

      {/* Recurring Edit Scope Modal */}
      {recurringEditTarget && (
        <RecurringEditScopeModal
          onOnlyThis={editOnlyThisOccurrence}
          onThisAndFuture={editThisAndFuture}
          onAll={editEntireSeries}
          onCancel={() => setRecurringEditTarget(null)}
        />
      )}

      {/* Recurring Delete Options Modal */}
      {recurringDeleteTarget && (
        <RecurringDeleteScopeModal
          onOnlyThis={deleteOnlyThisOccurrence}
          onThisAndFuture={deleteThisAndFuture}
          onAll={deleteEntireSeries}
          onCancel={() => setRecurringDeleteTarget(null)}
        />
      )}

      {/* Label Manager Modal */}
      {showLabelManager && (
        <LabelManager
          onClose={() => setShowLabelManager(false)}
          holidaySettings={holidaySettings}
          updateHolidaySettings={updateHolidaySettings}
          categories={categories}
          birthdaySettings={birthdaySettings}
          sortedCategories={sortedCategories}
          updateLabel={updateLabel}
          showHolidayColorPicker={showHolidayColorPicker}
          setShowHolidayColorPicker={setShowHolidayColorPicker}
        />
      )}
    </div>
  );
};

export default Calendar;
